import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiMail,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiSettings,
  FiX,
} from "react-icons/fi";
import { SEO } from "@/components/seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type SubscriberInfo = {
  email: string;
  status: string;
  preferences: {
    newsletter: boolean;
    rateAlerts: boolean;
    marketUpdates: boolean;
    promotions: boolean;
  };
};

const UNSUBSCRIBE_REASONS = [
  "Too many emails",
  "Content not relevant",
  "I never signed up",
  "Found a better service",
  "Other",
];

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriber, setSubscriber] = useState<SubscriberInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing unsubscribe token. Please use the link from your email.");
      setIsLoading(false);
      return;
    }

    fetch(`/api/unsubscribe/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error("Invalid or expired link");
        return res.json();
      })
      .then((data) => {
        setSubscriber(data as SubscriberInfo);
        if ((data as SubscriberInfo).status === "unsubscribed") {
          setIsUnsubscribed(true);
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to verify unsubscribe link");
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      const reason = selectedReason === "Other" ? customReason : selectedReason;

      const res = await fetch(`/api/unsubscribe/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unsubscribeAll: true,
          reason: reason || undefined,
        }),
      });

      const data = (await res.json()) as { message?: string; error?: string };

      if (!res.ok) {
        throw new Error(data.error || "Failed to unsubscribe");
      }

      setIsUnsubscribed(true);
      toast.success("Successfully unsubscribed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to unsubscribe");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FiLoader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SEO title="Unsubscribe" url="/unsubscribe" />
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold mb-2">Invalid Link</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button asChild>
                <Link to="/">Return Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (isUnsubscribed) {
    return (
      <>
        <SEO title="Unsubscribed" url="/unsubscribe" />
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-xl font-semibold mb-2">You're Unsubscribed</h1>
              <p className="text-muted-foreground mb-6">
                You have been removed from our mailing list and will no longer receive emails from us.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Changed your mind?{" "}
                <Link to="/subscribe" className="text-primary hover:underline">
                  Subscribe again
                </Link>
              </p>
              <Button asChild variant="outline">
                <Link to="/">Return Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title="Unsubscribe" url="/unsubscribe" />

      <div className="min-h-[60vh] py-16 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMail className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-serif text-3xl font-medium mb-2">
              Manage Email Preferences
            </h1>
            <p className="text-muted-foreground">
              You're managing preferences for <strong>{subscriber?.email}</strong>
            </p>
          </div>

          {!showPreferences ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What would you like to do?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-4"
                  onClick={() => setShowPreferences(true)}
                >
                  <FiSettings className="w-5 h-5 mr-3 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">Update Preferences</p>
                    <p className="text-sm text-muted-foreground">
                      Choose which emails you'd like to receive
                    </p>
                  </div>
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <div className="flex items-start gap-3">
                    <FiX className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-red-800">Unsubscribe from all emails</p>
                      <p className="text-sm text-red-700 mb-3">
                        You will no longer receive any communications from us.
                      </p>

                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-red-800">
                            Help us improve (optional)
                          </label>
                          <select
                            value={selectedReason}
                            onChange={(e) => setSelectedReason(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-red-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <option value="">Select a reason...</option>
                            {UNSUBSCRIBE_REASONS.map((reason) => (
                              <option key={reason} value={reason}>
                                {reason}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedReason === "Other" && (
                          <input
                            type="text"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            placeholder="Please tell us why..."
                            className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        )}

                        <Button
                          variant="destructive"
                          className="w-full"
                          onClick={handleUnsubscribe}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <FiX className="w-4 h-4 mr-2" />
                          )}
                          Unsubscribe from All
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <PreferencesForm
              token={token!}
              preferences={subscriber!.preferences}
              onBack={() => setShowPreferences(false)}
            />
          )}
        </div>
      </div>
    </>
  );
}

function PreferencesForm({
  token,
  preferences,
  onBack,
}: {
  token: string;
  preferences: SubscriberInfo["preferences"];
  onBack: () => void;
}) {
  const [prefs, setPrefs] = useState(preferences);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/subscribe/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, preferences: prefs }),
      });

      const data = (await res.json()) as { message?: string; error?: string };

      if (!res.ok) {
        throw new Error(data.error || "Failed to save preferences");
      }

      setIsSaved(true);
      toast.success("Preferences saved successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSaved) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheck className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Preferences Updated</h2>
          <p className="text-muted-foreground mb-6">
            Your email preferences have been saved.
          </p>
          <Button asChild variant="outline">
            <Link to="/">Return Home</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const prefOptions = [
    {
      key: "newsletter" as const,
      label: "Newsletter",
      description: "Monthly updates, tips, and industry news",
    },
    {
      key: "rateAlerts" as const,
      label: "Rate Alerts",
      description: "Notifications when interest rates change",
    },
    {
      key: "marketUpdates" as const,
      label: "Market Updates",
      description: "Property market trends and analysis",
    },
    {
      key: "promotions" as const,
      label: "Promotions",
      description: "Special offers and exclusive deals",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            &larr;
          </button>
          <CardTitle className="text-lg">Email Preferences</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Select which types of emails you'd like to receive:
        </p>

        <div className="space-y-3">
          {prefOptions.map((opt) => (
            <label
              key={opt.key}
              className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <input
                type="checkbox"
                checked={prefs[opt.key]}
                onChange={(e) =>
                  setPrefs({ ...prefs, [opt.key]: e.target.checked })
                }
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div>
                <p className="font-medium">{opt.label}</p>
                <p className="text-sm text-muted-foreground">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="pt-4 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? (
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FiCheck className="w-4 h-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
